"use client";

import { useTranslations } from "next-intl";
import { ContactChannels } from "./contact-channels";
import { ContactForm } from "./contact-form";
import { ContactQuickActions } from "./contact-quick-actions";

/**
 * The whole contact experience in one band.
 *
 * The form comes first in DOM order at every breakpoint, so it also leads the
 * reading and tab order; the desktop side-by-side arrangement is produced by
 * the grid's column assignment, not by source order. Previously a 400px image
 * preceded the form on mobile, putting decoration ahead of the task.
 *
 * No heading of its own: `PageTitleHeader` sits directly above this section and
 * owns the page's <h1> with the same `pages.contact` copy, as it does on every
 * other inner marketing page. Adding one here would duplicate that sentence and
 * give the page two <h1>s.
 */
export function ContactSection() {
  const info = useTranslations("contactInfo");

  return (
    <section className="bg-site py-12 font-outfit sm:py-16 lg:py-20">
      <div className="mx-auto w-full max-w-[1400px] px-4 md:px-10 lg:px-16">
        {/*
          Above the form, on narrow screens only: at those widths the channels
          rail is a whole form's scroll away, and calling is the faster path for
          anyone who is not here to fill anything in.
        */}
        <ContactQuickActions className="mb-8" />

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          {/* Form first in source; placed second visually on desktop. */}
          <div className="lg:col-span-7 lg:col-start-6 lg:row-start-1">
            <ContactForm />
          </div>
          <div className="lg:col-span-5 lg:col-start-1 lg:row-start-1">
            {/*
              The column needs a heading of its own: without one, the four
              channel <h3>s read as subsections of the form's <h2>, so the
              outline claims the phone number is part of the service request.
            */}
            <h2 className="mb-3 text-2xl font-semibold text-site-foreground">
              {info("heading")}
            </h2>
            <p className="mb-8 text-base text-site-foreground/70">
              {info("sectionBody")}
            </p>
            <ContactChannels />
          </div>
        </div>
      </div>
    </section>
  );
}
